export const investingMistakesArticleTranslations = {
  en: {
    investingMistakes: {
      title: "Common Investing Mistakes and How to Avoid Them",
      subtitle:
        "Learn from these common pitfalls and develop better investment strategies",
      cta: "Start Investing Wisely",
      section1: {
        heading: "Why Avoiding Mistakes Matters",
        text1:
          "Investing mistakes can be costly and set back your financial goals by years. Understanding common pitfalls helps you make better decisions and protect your wealth.",
        text2:
          "Even experienced investors make mistakes, but learning from others' experiences can help you avoid the most damaging errors and build a more successful investment strategy.",
      },
      section2: {
        heading: "Most Common Investing Mistakes",
        mistake1: {
          title: "Emotional Investing",
          text: "Making decisions based on fear or greed rather than logic and research. This often leads to buying high during market euphoria and selling low during panic.",
        },
        mistake2: {
          title: "Lack of Diversification",
          text: "Putting all your money in one stock, sector, or asset class. This concentrates risk and can lead to significant losses if that investment performs poorly.",
        },
        mistake3: {
          title: "Trying to Time the Market",
          text: "Attempting to predict market movements and buy at the lowest point while selling at the highest. Even professionals struggle with market timing.",
        },
        mistake4: {
          title: "Not Having a Plan",
          text: "Investing without clear goals, timeline, or strategy. This leads to inconsistent decisions and poor long-term results.",
        },
      },
      section3: {
        heading: "How to Avoid These Mistakes",
        solution1: {
          title: "Develop an Investment Plan",
          text: "Create a clear strategy based on your goals, risk tolerance, and timeline. Stick to your plan even when emotions run high.",
        },
        solution2: {
          title: "Diversify Your Portfolio",
          text: "Spread your investments across different asset classes, sectors, and geographic regions to reduce risk.",
        },
        solution3: {
          title: "Focus on Long-Term Investing",
          text: "Avoid trying to time the market. Instead, invest regularly and stay invested for the long term to benefit from compound growth.",
        },
        solution4: {
          title: "Educate Yourself Continuously",
          text: "Keep learning about investing principles, market dynamics, and new opportunities. Knowledge is your best defense against costly mistakes.",
        },
      },
      section4: {
        heading: "Additional Mistakes to Avoid",
        mistake5: {
          title: "Chasing Hot Tips",
          text: "Following stock tips from friends, social media, or financial media without doing your own research.",
        },
        mistake6: {
          title: "Ignoring Fees and Expenses",
          text: "Not considering how management fees, transaction costs, and taxes can erode your returns over time.",
        },
        mistake7: {
          title: "Overconfidence",
          text: "Believing you can consistently beat the market or that past success guarantees future results.",
        },
        mistake8: {
          title: "Not Rebalancing",
          text: "Failing to periodically adjust your portfolio to maintain your desired asset allocation as market values change.",
        },
      },
      conclusion: {
        title: "Building a Better Investment Strategy",
        text: "Successful investing isn't about avoiding all mistakes—it's about learning from them and developing a disciplined approach. By understanding these common pitfalls and implementing sound investment principles, you can improve your chances of achieving your financial goals and building long-term wealth.",
        cta: "Start Your Investment Journey",
      },
      section5: {
        heading: "Psychological Traps in Investing",
        intro: "Understanding the psychological factors that drive poor investment decisions is crucial for developing a rational approach to investing. Our minds often work against us when it comes to financial decisions.",
        trap1: {
          title: "Confirmation Bias",
          text: "Seeking information that confirms existing beliefs while ignoring contradictory evidence. This leads to overconfidence in poor investment choices."
        },
        trap2: {
          title: "Anchoring Bias",
          text: "Relying too heavily on the first piece of information encountered. Investors often anchor to purchase prices and refuse to sell losing positions."
        },
        trap3: {
          title: "Recency Bias",
          text: "Giving greater weight to recent events when making decisions. This can lead to buying after market highs and selling after lows."
        },
        trap4: {
          title: "Loss Aversion",
          text: "The tendency to feel losses more intensely than equivalent gains. This often results in holding losing investments too long and selling winners too quickly."
        }
      },
      section6: {
        heading: "Age-Specific Investment Mistakes",
        youngInvestors: {
          title: "Young Investors (20s-30s)",
          description: "While time is on their side, young investors often make mistakes that can compound over decades and significantly impact their wealth building.",
          mistake1: {
            title: "Being Too Conservative",
            text: "Playing it too safe with savings accounts when they have decades to ride out market volatility and benefit from compound growth."
          },
          mistake2: {
            title: "Not Starting Early Enough",
            text: "Delaying investment due to small amounts available, missing out on the powerful effect of compound interest over time."
          }
        },
        middleAged: {
          title: "Middle-Aged Investors (40s-50s)",
          description: "Balancing current financial responsibilities with retirement planning creates unique challenges and common mistakes for this age group.",
          mistake1: {
            title: "Panic Adjustments",
            text: "Making dramatic portfolio changes during market downturns, often buying high and selling low at the worst possible times."
          },
          mistake2: {
            title: "Neglecting Catch-Up Contributions",
            text: "Failing to take advantage of higher contribution limits available to those over 50, missing opportunities to accelerate retirement savings."
          }
        },
        retirementAge: {
          title: "Retirement-Age Investors (60+)",
          description: "Those near or in retirement face the challenge of preserving wealth while still generating returns to combat inflation and fund their golden years.",
          mistake1: {
            title: "Being Too Conservative",
            text: "Moving entirely to cash and bonds, failing to maintain some growth investments needed to keep pace with inflation over a potentially long retirement."
          },
          mistake2: {
            title: "Withdrawing Too Much Too Soon",
            text: "Taking excessive withdrawals early in retirement, potentially depleting savings and leaving insufficient funds for later years."
          }
        }
      },
      section7: {
        heading: "Technology and Modern Investment Mistakes",
        intro: "The digital age has brought new investment opportunities and tools, but also new ways to make costly mistakes. Understanding these modern pitfalls is essential for today's investors.",
        mistake1: {
          title: "Day Trading Apps",
          text: "Using mobile apps to make frequent trades based on emotions or tips, often resulting in high fees and poor returns compared to patient investing."
        },
        mistake2: {
          title: "Social Media Influence",
          text: "Making investment decisions based on social media posts, influencers, or online forums without proper research or understanding of personal financial goals."
        },
        mistake3: {
          title: "Algorithm Overreliance",
          text: "Blindly following robo-advisor suggestions without understanding the underlying strategy or ensuring it aligns with personal circumstances and goals."
        }
      },
      section8: {
        heading: "Creating a Mistake-Prevention System",
        intro: "The best way to avoid investment mistakes is to create systematic approaches and safeguards that help you stay disciplined even when emotions run high.",
        steps: {
          title: "Building Your Prevention System"
        },
        step1: {
          title: "Write Down Your Investment Plan",
          text: "Document your goals, risk tolerance, time horizon, and strategy. Having it written down makes it harder to deviate during emotional moments."
        },
        step2: {
          title: "Set Up Automatic Investing",
          text: "Automate regular contributions to remove emotion and timing decisions from the equation. Dollar-cost averaging helps smooth out market volatility."
        },
        step3: {
          title: "Create Decision Rules",
          text: "Establish clear criteria for buying, selling, and rebalancing. Having predetermined rules helps avoid emotional decision-making during market turbulence."
        },
        step4: {
          title: "Schedule Regular Reviews",
          text: "Set specific times for portfolio reviews rather than checking constantly. This reduces the temptation to make unnecessary changes based on short-term movements."
        },
        checklist: {
          title: "Pre-Investment Checklist",
          item1: "Does this investment align with my written plan and goals?",
          item2: "Have I researched this thoroughly beyond social media or tips?",
          item3: "Am I investing money I can afford to lose or lock up?",
          item4: "Am I making this decision based on logic rather than emotion?",
          item5: "How does this fit into my overall portfolio diversification?"
        }
      },
      section9: {
        heading: "Learning from Market History",
        intro: "Market history provides valuable lessons about common investor mistakes during different economic cycles. Understanding these patterns can help you avoid repeating them.",
        lesson1: {
          title: "Market Cycles Are Normal",
          text: "Every generation experiences market crashes and recoveries. Those who stay invested through complete cycles generally fare better than those who try to time the market."
        },
        lesson2: {
          title: "Bubbles and Manias Repeat",
          text: "Throughout history, investors have repeatedly gotten caught up in speculative bubbles, from tulips to technology stocks. Recognizing the signs can help you avoid these traps."
        },
        lesson3: {
          title: "Time in Market Beats Timing the Market",
          text: "Historical data consistently shows that investors who stay invested over long periods outperform those who try to move in and out of the market based on predictions."
        }
      }
    },
  },
  es: {
    investingMistakes: {
      title: "Errores Comunes de Inversión y Cómo Evitarlos",
      subtitle:
        "Aprende de estos errores comunes y desarrolla mejores estrategias de inversión",
      cta: "Comienza a Invertir Sabiamente",
      section1: {
        heading: "Por Qué Importa Evitar Errores",
        text1:
          "Los errores de inversión pueden ser costosos y retrasar tus objetivos financieros por años. Entender las trampas comunes te ayuda a tomar mejores decisiones y proteger tu riqueza.",
        text2:
          "Incluso los inversores experimentados cometen errores, pero aprender de las experiencias de otros puede ayudarte a evitar los errores más dañinos y construir una estrategia de inversión más exitosa.",
      },
      section2: {
        heading: "Errores de Inversión Más Comunes",
        mistake1: {
          title: "Inversión Emocional",
          text: "Tomar decisiones basadas en miedo o codicia en lugar de lógica e investigación. Esto a menudo lleva a comprar alto durante la euforia del mercado y vender bajo durante el pánico.",
        },
        mistake2: {
          title: "Falta de Diversificación",
          text: "Poner todo tu dinero en una acción, sector o clase de activo. Esto concentra el riesgo y puede llevar a pérdidas significativas si esa inversión tiene un mal rendimiento.",
        },
        mistake3: {
          title: "Intentar Cronometrar el Mercado",
          text: "Intentar predecir movimientos del mercado y comprar en el punto más bajo mientras vendes en el más alto. Incluso los profesionales luchan con el timing del mercado.",
        },
        mistake4: {
          title: "No Tener un Plan",
          text: "Invertir sin objetivos claros, cronograma o estrategia. Esto lleva a decisiones inconsistentes y resultados pobres a largo plazo.",
        },
      },
      section3: {
        heading: "Cómo Evitar Estos Errores",
        solution1: {
          title: "Desarrolla un Plan de Inversión",
          text: "Crea una estrategia clara basada en tus objetivos, tolerancia al riesgo y cronograma. Mantente fiel a tu plan incluso cuando las emociones estén altas.",
        },
        solution2: {
          title: "Diversifica tu Portafolio",
          text: "Distribuye tus inversiones entre diferentes clases de activos, sectores y regiones geográficas para reducir el riesgo.",
        },
        solution3: {
          title: "Enfócate en Inversión a Largo Plazo",
          text: "Evita intentar cronometrar el mercado. En su lugar, invierte regularmente y mantente invertido a largo plazo para beneficiarte del crecimiento compuesto.",
        },
        solution4: {
          title: "Edúcate Continuamente",
          text: "Sigue aprendiendo sobre principios de inversión, dinámicas del mercado y nuevas oportunidades. El conocimiento es tu mejor defensa contra errores costosos.",
        },
      },
      section4: {
        heading: "Errores Adicionales a Evitar",
        mistake5: {
          title: "Perseguir Consejos Calientes",
          text: "Seguir consejos de acciones de amigos, redes sociales o medios financieros sin hacer tu propia investigación.",
        },
        mistake6: {
          title: "Ignorar Tarifas y Gastos",
          text: "No considerar cómo las tarifas de gestión, costos de transacción e impuestos pueden erosionar tus rendimientos con el tiempo.",
        },
        mistake7: {
          title: "Exceso de Confianza",
          text: "Creer que puedes consistentemente superar al mercado o que el éxito pasado garantiza resultados futuros.",
        },
        mistake8: {
          title: "No Rebalancear",
          text: "Fallar en ajustar periódicamente tu portafolio para mantener tu asignación de activos deseada a medida que cambian los valores del mercado.",
        },
      },
      conclusion: {
        title: "Construyendo una Mejor Estrategia de Inversión",
        text: "La inversión exitosa no se trata de evitar todos los errores—se trata de aprender de ellos y desarrollar un enfoque disciplinado. Al entender estas trampas comunes e implementar principios sólidos de inversión, puedes mejorar tus posibilidades de lograr tus objetivos financieros y construir riqueza a largo plazo.",
        cta: "Comienza tu Viaje de Inversión",
      },
      section5: {
        heading: "Trampas Psicológicas en la Inversión",
        intro: "Entender los factores psicológicos que impulsan las malas decisiones de inversión es crucial para desarrollar un enfoque racional hacia la inversión. Nuestras mentes a menudo trabajan en nuestra contra cuando se trata de decisiones financieras.",
        trap1: {
          title: "Sesgo de Confirmación",
          text: "Buscar información que confirme creencias existentes mientras se ignora evidencia contradictoria. Esto lleva al exceso de confianza en malas decisiones de inversión."
        },
        trap2: {
          title: "Sesgo de Anclaje",
          text: "Depender demasiado de la primera información encontrada. Los inversores a menudo se anclan a los precios de compra y se niegan a vender posiciones perdedoras."
        },
        trap3: {
          title: "Sesgo de Recencia",
          text: "Dar mayor peso a eventos recientes al tomar decisiones. Esto puede llevar a comprar después de máximos del mercado y vender después de mínimos."
        },
        trap4: {
          title: "Aversión a las Pérdidas",
          text: "La tendencia a sentir las pérdidas más intensamente que las ganancias equivalentes. Esto a menudo resulta en mantener inversiones perdedoras demasiado tiempo y vender ganadoras muy rápido."
        }
      },
      section6: {
        heading: "Errores de Inversión Específicos por Edad",
        youngInvestors: {
          title: "Inversores Jóvenes (20s-30s)",
          description: "Aunque el tiempo está de su lado, los inversores jóvenes a menudo cometen errores que pueden acumularse durante décadas y impactar significativamente su construcción de riqueza.",
          mistake1: {
            title: "Ser Demasiado Conservador",
            text: "Jugar demasiado seguro con cuentas de ahorro cuando tienen décadas para superar la volatilidad del mercado y beneficiarse del crecimiento compuesto."
          },
          mistake2: {
            title: "No Empezar lo Suficientemente Temprano",
            text: "Retrasar la inversión debido a pequeñas cantidades disponibles, perdiendo el poderoso efecto del interés compuesto con el tiempo."
          }
        },
        middleAged: {
          title: "Inversores de Mediana Edad (40s-50s)",
          description: "Equilibrar las responsabilidades financieras actuales con la planificación de la jubilación crea desafíos únicos y errores comunes para este grupo de edad.",
          mistake1: {
            title: "Ajustes de Pánico",
            text: "Hacer cambios dramáticos en el portafolio durante caídas del mercado, a menudo comprando alto y vendiendo bajo en los peores momentos posibles."
          },
          mistake2: {
            title: "Descuidar las Contribuciones de Recuperación",
            text: "Fallar en aprovechar los límites de contribución más altos disponibles para mayores de 50, perdiendo oportunidades de acelerar el ahorro para la jubilación."
          }
        },
        retirementAge: {
          title: "Inversores en Edad de Jubilación (60+)",
          description: "Aquellos cerca o en jubilación enfrentan el desafío de preservar la riqueza mientras generan rendimientos para combatir la inflación y financiar sus años dorados.",
          mistake1: {
            title: "Ser Demasiado Conservador",
            text: "Moverse completamente a efectivo y bonos, fallando en mantener algunas inversiones de crecimiento necesarias para mantenerse al ritmo de la inflación durante una jubilación potencialmente larga."
          },
          mistake2: {
            title: "Retirar Demasiado Muy Pronto",
            text: "Tomar retiros excesivos temprano en la jubilación, potencialmente agotando los ahorros y dejando fondos insuficientes para años posteriores."
          }
        }
      },
      section7: {
        heading: "Tecnología y Errores de Inversión Modernos",
        intro: "La era digital ha traído nuevas oportunidades y herramientas de inversión, pero también nuevas formas de cometer errores costosos. Entender estas trampas modernas es esencial para los inversores de hoy.",
        mistake1: {
          title: "Apps de Trading Diario",
          text: "Usar aplicaciones móviles para hacer intercambios frecuentes basados en emociones o consejos, a menudo resultando en altas tarifas y malos rendimientos comparados con la inversión paciente."
        },
        mistake2: {
          title: "Influencia de Redes Sociales",
          text: "Tomar decisiones de inversión basadas en publicaciones de redes sociales, influencers o foros en línea sin investigación adecuada o entendimiento de objetivos financieros personales."
        },
        mistake3: {
          title: "Sobredependencia de Algoritmos",
          text: "Seguir ciegamente sugerencias de robo-advisors sin entender la estrategia subyacente o asegurar que se alinee con circunstancias y objetivos personales."
        }
      },
      section8: {
        heading: "Creando un Sistema de Prevención de Errores",
        intro: "La mejor manera de evitar errores de inversión es crear enfoques sistemáticos y salvaguardas que te ayuden a mantenerte disciplinado incluso cuando las emociones están altas.",
        steps: {
          title: "Construyendo tu Sistema de Prevención"
        },
        step1: {
          title: "Escribe tu Plan de Inversión",
          text: "Documenta tus objetivos, tolerancia al riesgo, horizonte temporal y estrategia. Tenerlo escrito hace más difícil desviarse durante momentos emocionales."
        },
        step2: {
          title: "Configura Inversión Automática",
          text: "Automatiza contribuciones regulares para quitar la emoción y decisiones de timing de la ecuación. El promedio de costo en dólares ayuda a suavizar la volatilidad del mercado."
        },
        step3: {
          title: "Crea Reglas de Decisión",
          text: "Establece criterios claros para comprar, vender y rebalancear. Tener reglas predeterminadas ayuda a evitar la toma de decisiones emocional durante la turbulencia del mercado."
        },
        step4: {
          title: "Programa Revisiones Regulares",
          text: "Establece tiempos específicos para revisiones de portafolio en lugar de verificar constantemente. Esto reduce la tentación de hacer cambios innecesarios basados en movimientos a corto plazo."
        },
        checklist: {
          title: "Lista de Verificación Pre-Inversión",
          item1: "¿Esta inversión se alinea con mi plan escrito y objetivos?",
          item2: "¿He investigado esto completamente más allá de redes sociales o consejos?",
          item3: "¿Estoy invirtiendo dinero que puedo permitirme perder o bloquear?",
          item4: "¿Estoy tomando esta decisión basada en lógica en lugar de emoción?",
          item5: "¿Cómo encaja esto en la diversificación general de mi portafolio?"
        }
      },
      section9: {
        heading: "Aprendiendo de la Historia del Mercado",
        intro: "La historia del mercado proporciona lecciones valiosas sobre errores comunes de inversores durante diferentes ciclos económicos. Entender estos patrones puede ayudarte a evitar repetirlos.",
        lesson1: {
          title: "Los Ciclos del Mercado son Normales",
          text: "Cada generación experimenta crashes y recuperaciones del mercado. Aquellos que se mantienen invertidos a través de ciclos completos generalmente les va mejor que aquellos que tratan de cronometrar el mercado."
        },
        lesson2: {
          title: "Las Burbujas y Manías se Repiten",
          text: "A lo largo de la historia, los inversores han sido repetidamente atrapados en burbujas especulativas, desde tulipanes hasta acciones tecnológicas. Reconocer las señales puede ayudarte a evitar estas trampas."
        },
        lesson3: {
          title: "Tiempo en el Mercado Supera a Cronometrar el Mercado",
          text: "Los datos históricos muestran consistentemente que los inversores que se mantienen invertidos durante largos períodos superan a aquellos que tratan de entrar y salir del mercado basados en predicciones."
        }
      }
    },
  },
  fr: {
    investingMistakes: {
      title: "Erreurs d'Investissement Courantes et Comment les Éviter",
      subtitle:
        "Apprenez de ces pièges communs et développez de meilleures stratégies d'investissement",
      cta: "Commencez à Investir Intelligemment",
      section1: {
        heading: "Pourquoi Éviter les Erreurs Importe",
        text1:
          "Les erreurs d'investissement peuvent être coûteuses et retarder vos objectifs financiers de plusieurs années. Comprendre les pièges communs vous aide à prendre de meilleures décisions et protéger votre richesse.",
        text2:
          "Même les investisseurs expérimentés font des erreurs, mais apprendre des expériences d'autrui peut vous aider à éviter les erreurs les plus dommageables et construire une stratégie d'investissement plus réussie.",
      },
      section2: {
        heading: "Erreurs d'Investissement les Plus Courantes",
        mistake1: {
          title: "Investissement Émotionnel",
          text: "Prendre des décisions basées sur la peur ou la cupidité plutôt que sur la logique et la recherche. Cela mène souvent à acheter haut pendant l'euphorie du marché et vendre bas pendant la panique.",
        },
        mistake2: {
          title: "Manque de Diversification",
          text: "Mettre tout votre argent dans une action, un secteur ou une classe d'actifs. Cela concentre le risque et peut mener à des pertes significatives si cet investissement performe mal.",
        },
        mistake3: {
          title: "Essayer de Chronométrer le Marché",
          text: "Tenter de prédire les mouvements du marché et acheter au point le plus bas tout en vendant au plus haut. Même les professionnels luttent avec le timing du marché.",
        },
        mistake4: {
          title: "Ne Pas Avoir de Plan",
          text: "Investir sans objectifs clairs, calendrier ou stratégie. Cela mène à des décisions incohérentes et de mauvais résultats à long terme.",
        },
      },
      section3: {
        heading: "Comment Éviter Ces Erreurs",
        solution1: {
          title: "Développer un Plan d'Investissement",
          text: "Créez une stratégie claire basée sur vos objectifs, tolérance au risque et calendrier. Tenez-vous à votre plan même quand les émotions sont fortes.",
        },
        solution2: {
          title: "Diversifiez Votre Portefeuille",
          text: "Répartissez vos investissements entre différentes classes d'actifs, secteurs et régions géographiques pour réduire le risque.",
        },
        solution3: {
          title: "Concentrez-vous sur l'Investissement à Long Terme",
          text: "Évitez d'essayer de chronométrer le marché. Au lieu de cela, investissez régulièrement et restez investi à long terme pour bénéficier de la croissance composée.",
        },
        solution4: {
          title: "Éduquez-vous Continuellement",
          text: "Continuez à apprendre sur les principes d'investissement, les dynamiques du marché et les nouvelles opportunités. La connaissance est votre meilleure défense contre les erreurs coûteuses.",
        },
      },
      section4: {
        heading: "Erreurs Supplémentaires à Éviter",
        mistake5: {
          title: "Poursuivre les Conseils Chauds",
          text: "Suivre des conseils d'actions d'amis, des réseaux sociaux ou des médias financiers sans faire vos propres recherches.",
        },
        mistake6: {
          title: "Ignorer les Frais et Dépenses",
          text: "Ne pas considérer comment les frais de gestion, coûts de transaction et taxes peuvent éroder vos rendements au fil du temps.",
        },
        mistake7: {
          title: "Excès de Confiance",
          text: "Croire que vous pouvez consistamment battre le marché ou que le succès passé garantit les résultats futurs.",
        },
        mistake8: {
          title: "Ne Pas Rééquilibrer",
          text: "Échouer à ajuster périodiquement votre portefeuille pour maintenir votre allocation d'actifs désirée alors que les valeurs du marché changent.",
        },
      },
      conclusion: {
        title: "Construire une Meilleure Stratégie d'Investissement",
        text: "L'investissement réussi ne consiste pas à éviter toutes les erreurs—il s'agit d'apprendre d'elles et de développer une approche disciplinée. En comprenant ces pièges communs et en implémentant des principes d'investissement solides, vous pouvez améliorer vos chances d'atteindre vos objectifs financiers et construire une richesse à long terme.",
        cta: "Commencez Votre Voyage d'Investissement",
      },
      section5: {
        heading: "Pièges Psychologiques dans l'Investissement",
        intro: "Comprendre les facteurs psychologiques qui entraînent de mauvaises décisions d'investissement est crucial pour développer une approche rationnelle de l'investissement. Nos esprits travaillent souvent contre nous en matière de décisions financières.",
        trap1: {
          title: "Biais de Confirmation",
          text: "Rechercher des informations qui confirment les croyances existantes tout en ignorant les preuves contradictoires. Cela mène à un excès de confiance dans de mauvais choix d'investissement."
        },
        trap2: {
          title: "Biais d'Ancrage",
          text: "S'appuyer trop lourdement sur la première information rencontrée. Les investisseurs s'ancrent souvent aux prix d'achat et refusent de vendre des positions perdantes."
        },
        trap3: {
          title: "Biais de Récence",
          text: "Donner plus de poids aux événements récents lors de la prise de décisions. Cela peut mener à acheter après des sommets de marché et vendre après des creux."
        },
        trap4: {
          title: "Aversion aux Pertes",
          text: "La tendance à ressentir les pertes plus intensément que les gains équivalents. Cela résulte souvent en gardant des investissements perdants trop longtemps et vendant des gagnants trop rapidement."
        }
      },
      section6: {
        heading: "Erreurs d'Investissement Spécifiques à l'Âge",
        youngInvestors: {
          title: "Jeunes Investisseurs (20-30 ans)",
          description: "Bien que le temps soit de leur côté, les jeunes investisseurs font souvent des erreurs qui peuvent se composer sur des décennies et impacter significativement leur construction de richesse.",
          mistake1: {
            title: "Être Trop Conservateur",
            text: "Jouer trop prudemment avec des comptes d'épargne quand ils ont des décennies pour surmonter la volatilité du marché et bénéficier de la croissance composée."
          },
          mistake2: {
            title: "Ne Pas Commencer Assez Tôt",
            text: "Retarder l'investissement à cause de petites sommes disponibles, ratant le puissant effet de l'intérêt composé dans le temps."
          }
        },
        middleAged: {
          title: "Investisseurs d'Âge Moyen (40-50 ans)",
          description: "Équilibrer les responsabilités financières actuelles avec la planification de retraite crée des défis uniques et des erreurs communes pour ce groupe d'âge.",
          mistake1: {
            title: "Ajustements de Panique",
            text: "Faire des changements dramatiques de portefeuille pendant les chutes de marché, souvent acheter haut et vendre bas aux pires moments possibles."
          },
          mistake2: {
            title: "Négliger les Contributions de Rattrapage",
            text: "Échouer à profiter des limites de contribution plus élevées disponibles pour les plus de 50 ans, ratant des opportunités d'accélérer l'épargne retraite."
          }
        },
        retirementAge: {
          title: "Investisseurs en Âge de Retraite (60+)",
          description: "Ceux près ou en retraite font face au défi de préserver la richesse tout en générant des rendements pour combattre l'inflation et financer leurs années dorées.",
          mistake1: {
            title: "Être Trop Conservateur",
            text: "Se déplacer entièrement vers l'argent liquide et les obligations, échouant à maintenir quelques investissements de croissance nécessaires pour suivre l'inflation pendant une retraite potentiellement longue."
          },
          mistake2: {
            title: "Retirer Trop Trop Tôt",
            text: "Prendre des retraits excessifs tôt dans la retraite, potentiellement épuisant les économies et laissant des fonds insuffisants pour les années ultérieures."
          }
        }
      },
      section7: {
        heading: "Technologie et Erreurs d'Investissement Modernes",
        intro: "L'ère numérique a apporté de nouvelles opportunités et outils d'investissement, mais aussi de nouvelles façons de faire des erreurs coûteuses. Comprendre ces pièges modernes est essentiel pour les investisseurs d'aujourd'hui.",
        mistake1: {
          title: "Applications de Trading Quotidien",
          text: "Utiliser des applications mobiles pour faire des échanges fréquents basés sur des émotions ou des conseils, souvent résultant en frais élevés et mauvais rendements comparés à l'investissement patient."
        },
        mistake2: {
          title: "Influence des Réseaux Sociaux",
          text: "Prendre des décisions d'investissement basées sur des publications de réseaux sociaux, influenceurs ou forums en ligne sans recherche appropriée ou compréhension des objectifs financiers personnels."
        },
        mistake3: {
          title: "Surdépendance aux Algorithmes",
          text: "Suivre aveuglément les suggestions de robo-conseillers sans comprendre la stratégie sous-jacente ou s'assurer qu'elle s'aligne avec les circonstances et objectifs personnels."
        }
      },
      section8: {
        heading: "Créer un Système de Prévention des Erreurs",
        intro: "La meilleure façon d'éviter les erreurs d'investissement est de créer des approches systématiques et des protections qui vous aident à rester discipliné même quand les émotions sont fortes.",
        steps: {
          title: "Construire Votre Système de Prévention"
        },
        step1: {
          title: "Écrivez Votre Plan d'Investissement",
          text: "Documentez vos objectifs, tolérance au risque, horizon temporel et stratégie. L'avoir écrit rend plus difficile de dévier pendant les moments émotionnels."
        },
        step2: {
          title: "Configurez l'Investissement Automatique",
          text: "Automatisez les contributions régulières pour enlever l'émotion et les décisions de timing de l'équation. La moyenne d'achat aide à lisser la volatilité du marché."
        },
        step3: {
          title: "Créez des Règles de Décision",
          text: "Établissez des critères clairs pour acheter, vendre et rééquilibrer. Avoir des règles prédéterminées aide à éviter la prise de décision émotionnelle pendant la turbulence du marché."
        },
        step4: {
          title: "Programmez des Révisions Régulières",
          text: "Fixez des temps spécifiques pour les révisions de portefeuille plutôt que de vérifier constamment. Cela réduit la tentation de faire des changements inutiles basés sur des mouvements à court terme."
        },
        checklist: {
          title: "Liste de Vérification Pré-Investissement",
          item1: "Cet investissement s'aligne-t-il avec mon plan écrit et mes objectifs?",
          item2: "Ai-je recherché cela complètement au-delà des réseaux sociaux ou conseils?",
          item3: "Est-ce que j'investis de l'argent que je peux me permettre de perdre ou bloquer?",
          item4: "Est-ce que je prends cette décision basée sur la logique plutôt que l'émotion?",
          item5: "Comment cela s'intègre-t-il dans la diversification globale de mon portefeuille?"
        }
      },
      section9: {
        heading: "Apprendre de l'Histoire du Marché",
        intro: "L'histoire du marché fournit des leçons précieuses sur les erreurs communes des investisseurs pendant différents cycles économiques. Comprendre ces modèles peut vous aider à éviter de les répéter.",
        lesson1: {
          title: "Les Cycles du Marché sont Normaux",
          text: "Chaque génération expérimente des crashes et récupérations de marché. Ceux qui restent investis à travers des cycles complets s'en sortent généralement mieux que ceux qui essaient de chronométrer le marché."
        },
        lesson2: {
          title: "Les Bulles et Manies se Répètent",
          text: "À travers l'histoire, les investisseurs ont été répétitivement pris dans des bulles spéculatives, des tulipes aux actions technologiques. Reconnaître les signes peut vous aider à éviter ces pièges."
        },
        lesson3: {
          title: "Le Temps dans le Marché Bat Chronométrer le Marché",
          text: "Les données historiques montrent constamment que les investisseurs qui restent investis sur de longues périodes surpassent ceux qui essaient d'entrer et sortir du marché basé sur des prédictions."
        }
      }
    },
  },
  de: {
    investingMistakes: {
      title: "Häufige Anlagefehler und Wie Man Sie Vermeidet",
      subtitle:
        "Lernen Sie aus diesen häufigen Fallstricken und entwickeln Sie bessere Anlagestrategien",
      cta: "Beginnen Sie Klug zu Investieren",
      section1: {
        heading: "Warum das Vermeiden von Fehlern Wichtig Ist",
        text1:
          "Anlagefehler können kostspielig sein und Ihre finanziellen Ziele um Jahre zurückwerfen. Das Verstehen häufiger Fallstricke hilft Ihnen, bessere Entscheidungen zu treffen und Ihr Vermögen zu schützen.",
        text2:
          "Selbst erfahrene Anleger machen Fehler, aber aus den Erfahrungen anderer zu lernen kann Ihnen helfen, die schädlichsten Fehler zu vermeiden und eine erfolgreichere Anlagestrategie aufzubauen.",
      },
      section2: {
        heading: "Häufigste Anlagefehler",
        mistake1: {
          title: "Emotionales Investieren",
          text: "Entscheidungen basierend auf Angst oder Gier anstatt auf Logik und Forschung zu treffen. Dies führt oft dazu, hoch zu kaufen während der Markteuphorie und niedrig zu verkaufen während der Panik.",
        },
        mistake2: {
          title: "Mangel an Diversifikation",
          text: "Ihr ganzes Geld in eine Aktie, einen Sektor oder eine Anlageklasse zu stecken. Dies konzentriert das Risiko und kann zu bedeutenden Verlusten führen, wenn diese Anlage schlecht abschneidet.",
        },
        mistake3: {
          title: "Versuchen, den Markt zu Timen",
          text: "Zu versuchen, Marktbewegungen vorherzusagen und am niedrigsten Punkt zu kaufen während man am höchsten verkauft. Selbst Profis kämpfen mit Market-Timing.",
        },
        mistake4: {
          title: "Keinen Plan Haben",
          text: "Zu investieren ohne klare Ziele, Zeitplan oder Strategie. Dies führt zu inkonsistenten Entscheidungen und schlechten langfristigen Ergebnissen.",
        },
      },
      section3: {
        heading: "Wie Man Diese Fehler Vermeidet",
        solution1: {
          title: "Einen Anlageplan Entwickeln",
          text: "Erstellen Sie eine klare Strategie basierend auf Ihren Zielen, Risikobereitschaft und Zeitplan. Halten Sie sich an Ihren Plan, auch wenn die Emotionen hochkochen.",
        },
        solution2: {
          title: "Diversifizieren Sie Ihr Portfolio",
          text: "Verteilen Sie Ihre Anlagen über verschiedene Anlageklassen, Sektoren und geografische Regionen, um das Risiko zu reduzieren.",
        },
        solution3: {
          title: "Fokus auf Langfristige Investitionen",
          text: "Vermeiden Sie es, den Markt zu timen. Investieren Sie stattdessen regelmäßig und bleiben Sie langfristig investiert, um von zusammengesetztem Wachstum zu profitieren.",
        },
        solution4: {
          title: "Bilden Sie Sich Kontinuierlich Weiter",
          text: "Lernen Sie weiterhin über Anlageprinzipien, Marktdynamiken und neue Möglichkeiten. Wissen ist Ihre beste Verteidigung gegen kostspielige Fehler.",
        },
      },
      section4: {
        heading: "Zusätzliche Fehler zu Vermeiden",
        mistake5: {
          title: "Heißen Tipps Nachjagen",
          text: "Aktientipps von Freunden, sozialen Medien oder Finanzmedien zu folgen, ohne eigene Recherche zu betreiben.",
        },
        mistake6: {
          title: "Gebühren und Ausgaben Ignorieren",
          text: "Nicht zu berücksichtigen, wie Verwaltungsgebühren, Transaktionskosten und Steuern Ihre Renditen über die Zeit erodieren können.",
        },
        mistake7: {
          title: "Selbstüberschätzung",
          text: "Zu glauben, dass Sie konsistent den Markt schlagen können oder dass vergangener Erfolg zukünftige Ergebnisse garantiert.",
        },
        mistake8: {
          title: "Nicht Rebalancieren",
          text: "Zu versäumen, Ihr Portfolio periodisch anzupassen, um Ihre gewünschte Anlagenallokation beizubehalten, während sich Marktwerte ändern.",
        },
      },
      conclusion: {
        title: "Aufbau einer Besseren Anlagestrategie",
        text: "Erfolgreiches Investieren bedeutet nicht, alle Fehler zu vermeiden—es bedeutet, von ihnen zu lernen und einen disziplinierten Ansatz zu entwickeln. Indem Sie diese häufigen Fallstricke verstehen und solide Anlageprinzipien implementieren, können Sie Ihre Chancen verbessern, Ihre finanziellen Ziele zu erreichen und langfristiges Vermögen aufzubauen.",
        cta: "Beginnen Sie Ihre Anlagereise",
      },
      section5: {
        heading: "Psychologische Fallen beim Investieren",
        intro: "Das Verstehen der psychologischen Faktoren, die schlechte Investitionsentscheidungen antreiben, ist entscheidend für die Entwicklung eines rationalen Ansatzes beim Investieren. Unsere Köpfe arbeiten oft gegen uns, wenn es um finanzielle Entscheidungen geht.",
        trap1: {
          title: "Bestätigungsfehler",
          text: "Das Suchen nach Informationen, die bestehende Überzeugungen bestätigen, während widersprüchliche Beweise ignoriert werden. Dies führt zu Selbstüberschätzung bei schlechten Investitionsentscheidungen."
        },
        trap2: {
          title: "Ankerverzerrung",
          text: "Sich zu stark auf die erste gefundene Information zu verlassen. Investoren verankern sich oft an Kaufpreise und weigern sich, Verlustpositionen zu verkaufen."
        },
        trap3: {
          title: "Verfügbarkeitsheuristik",
          text: "Jüngsten Ereignissen bei Entscheidungen mehr Gewicht zu geben. Dies kann dazu führen, nach Markthöchstständen zu kaufen und nach Tiefstständen zu verkaufen."
        },
        trap4: {
          title: "Verlustaversion",
          text: "Die Tendenz, Verluste intensiver zu empfinden als gleichwertige Gewinne. Dies führt oft dazu, Verlustinvestitionen zu lange zu halten und Gewinner zu schnell zu verkaufen."
        }
      },
      section6: {
        heading: "Altersspezifische Investitionsfehler",
        youngInvestors: {
          title: "Junge Investoren (20er-30er)",
          description: "Obwohl die Zeit auf ihrer Seite ist, machen junge Investoren oft Fehler, die sich über Jahrzehnte zusammensetzen und ihre Vermögensbildung erheblich beeinträchtigen können.",
          mistake1: {
            title: "Zu Konservativ Sein",
            text: "Zu sicher mit Sparkonten spielen, wenn sie Jahrzehnte haben, um Marktvolatilität zu überstehen und von zusammengesetztem Wachstum zu profitieren."
          },
          mistake2: {
            title: "Nicht Früh Genug Anfangen",
            text: "Investitionen aufgrund kleiner verfügbarer Beträge verzögern und den mächtigen Effekt des Zinseszinses über die Zeit verpassen."
          }
        },
        middleAged: {
          title: "Investoren Mittleren Alters (40er-50er)",
          description: "Das Ausbalancieren aktueller finanzieller Verantwortlichkeiten mit Ruhestandsplanung schafft einzigartige Herausforderungen und häufige Fehler für diese Altersgruppe.",
          mistake1: {
            title: "Panik-Anpassungen",
            text: "Dramatische Portfolio-Änderungen während Marktabschwüngen vornehmen, oft hoch kaufen und niedrig verkaufen zu den schlechtestmöglichen Zeiten."
          },
          mistake2: {
            title: "Aufholbeiträge Vernachlässigen",
            text: "Versäumen, die höheren Beitragsgrenzen für über 50-Jährige zu nutzen und Gelegenheiten zur Beschleunigung der Altersvorsorge zu verpassen."
          }
        },
        retirementAge: {
          title: "Investoren im Ruhestandsalter (60+)",
          description: "Diejenigen, die kurz vor oder im Ruhestand stehen, stehen vor der Herausforderung, Vermögen zu bewahren und gleichzeitig Renditen zu generieren, um Inflation zu bekämpfen und ihre goldenen Jahre zu finanzieren.",
          mistake1: {
            title: "Zu Konservativ Sein",
            text: "Vollständig zu Bargeld und Anleihen wechseln und versäumen, einige Wachstumsinvestitionen zu behalten, die nötig sind, um mit der Inflation während eines potenziell langen Ruhestands Schritt zu halten."
          },
          mistake2: {
            title: "Zu Viel Zu Früh Abheben",
            text: "Übermäßige Abhebungen früh im Ruhestand nehmen, potenziell Ersparnisse erschöpfen und unzureichende Mittel für spätere Jahre hinterlassen."
          }
        }
      },
      section7: {
        heading: "Technologie und Moderne Investitionsfehler",
        intro: "Das digitale Zeitalter hat neue Investitionsmöglichkeiten und Werkzeuge gebracht, aber auch neue Wege, kostspielige Fehler zu machen. Das Verstehen dieser modernen Fallstricke ist für heutige Investoren unerlässlich.",
        mistake1: {
          title: "Day-Trading-Apps",
          text: "Mobile Apps für häufige Trades basierend auf Emotionen oder Tipps verwenden, oft resultierend in hohen Gebühren und schlechten Renditen im Vergleich zu geduldigen Investitionen."
        },
        mistake2: {
          title: "Social-Media-Einfluss",
          text: "Investitionsentscheidungen basierend auf Social-Media-Posts, Influencern oder Online-Foren treffen, ohne angemessene Recherche oder Verständnis persönlicher finanzieller Ziele."
        },
        mistake3: {
          title: "Algorithmus-Überabhängigkeit",
          text: "Blind Robo-Advisor-Vorschlägen folgen, ohne die zugrundeliegende Strategie zu verstehen oder sicherzustellen, dass sie mit persönlichen Umständen und Zielen übereinstimmt."
        }
      },
      section8: {
        heading: "Ein Fehlerverhinderungssystem Schaffen",
        intro: "Der beste Weg, Investitionsfehler zu vermeiden, ist systematische Ansätze und Schutzmaßnahmen zu schaffen, die Ihnen helfen, diszipliniert zu bleiben, auch wenn Emotionen hochkochen.",
        steps: {
          title: "Ihr Verhinderungssystem Aufbauen"
        },
        step1: {
          title: "Schreiben Sie Ihren Investitionsplan Auf",
          text: "Dokumentieren Sie Ihre Ziele, Risikobereitschaft, Zeithorizont und Strategie. Es schriftlich zu haben macht es schwerer, in emotionalen Momenten abzuweichen."
        },
        step2: {
          title: "Automatisches Investieren Einrichten",
          text: "Automatisieren Sie regelmäßige Beiträge, um Emotion und Timing-Entscheidungen aus der Gleichung zu entfernen. Dollar-Cost-Averaging hilft, Marktvolatilität zu glätten."
        },
        step3: {
          title: "Entscheidungsregeln Erstellen",
          text: "Etablieren Sie klare Kriterien für Kauf, Verkauf und Neugewichtung. Vorbestimmte Regeln helfen, emotionale Entscheidungsfindung während Marktturbulenzen zu vermeiden."
        },
        step4: {
          title: "Regelmäßige Überprüfungen Planen",
          text: "Setzen Sie spezifische Zeiten für Portfolio-Überprüfungen fest, anstatt ständig zu überprüfen. Dies reduziert die Versuchung, unnötige Änderungen basierend auf kurzfristigen Bewegungen zu machen."
        },
        checklist: {
          title: "Vor-Investitions-Checkliste",
          item1: "Stimmt diese Investition mit meinem geschriebenen Plan und Zielen überein?",
          item2: "Habe ich dies gründlich jenseits von Social Media oder Tipps recherchiert?",
          item3: "Investiere ich Geld, das ich mir zu verlieren oder einzusperren leisten kann?",
          item4: "Treffe ich diese Entscheidung basierend auf Logik statt Emotion?",
          item5: "Wie passt dies in meine gesamte Portfolio-Diversifikation?"
        }
      },
      section9: {
        heading: "Von der Marktgeschichte Lernen",
        intro: "Die Marktgeschichte bietet wertvolle Lektionen über häufige Investorenfehler während verschiedener Wirtschaftszyklen. Das Verstehen dieser Muster kann Ihnen helfen, sie nicht zu wiederholen.",
        lesson1: {
          title: "Marktzyklen sind Normal",
          text: "Jede Generation erlebt Marktcrashs und Erholungen. Diejenigen, die durch komplette Zyklen investiert bleiben, schneiden generell besser ab als diejenigen, die versuchen, den Markt zu timen."
        },
        lesson2: {
          title: "Blasen und Manien Wiederholen Sich",
          text: "Durch die Geschichte hindurch wurden Investoren wiederholt in spekulative Blasen hineingezogen, von Tulpen bis zu Technologieaktien. Die Zeichen zu erkennen kann Ihnen helfen, diese Fallen zu vermeiden."
        },
        lesson3: {
          title: "Zeit im Markt Schlägt Market-Timing",
          text: "Historische Daten zeigen konstant, dass Investoren, die über lange Zeiträume investiert bleiben, diejenigen übertreffen, die versuchen, basierend auf Vorhersagen in und aus dem Markt zu gehen."
        }
      }
    },
  },
  hi: {
    investingMistakes: {
      title: "सामान्य निवेश गलतियाँ और उनसे कैसे बचें",
      subtitle:
        "इन सामान्य जालों से सीखें और बेहतर निवेश रणनीतियाँ विकसित करें",
      cta: "समझदारी से निवेश करना शुरू करें",
      section1: {
        heading: "गलतियों से बचना क्यों महत्वपूर्ण है",
        text1:
          "निवेश की गलतियाँ महंगी हो सकती हैं और आपके वित्तीय लक्ष्यों को वर्षों तक पीछे धकेल सकती हैं। सामान्य जालों को समझना आपको बेहतर निर्णय लेने और अपनी संपत्ति की रक्षा करने में मदद करता है।",
        text2:
          "अनुभवी निवेशक भी गलतियाँ करते हैं, लेकिन दूसरों के अनुभवों से सीखना आपको सबसे नुकसानदायक त्रुटियों से बचने और अधिक सफल निवेश रणनीति बनाने में मदद कर सकता है।",
      },
      section2: {
        heading: "सबसे सामान्य निवेश गलतियाँ",
        mistake1: {
          title: "भावनात्मक निवेश",
          text: "तर्क और अनुसंधान के बजाय डर या लालच के आधार पर निर्णय लेना। यह अक्सर बाज़ार की उत्साह के दौरान ऊंचा खरीदने और दहशत के दौरान कम बेचने की ओर ले जाता है।",
        },
        mistake2: {
          title: "विविधीकरण की कमी",
          text: "अपना सारा पैसा एक स्टॉक, सेक्टर या एसेट क्लास में डालना। यह जोखिम को केंद्रित करता है और यदि वह निवेश खराब प्रदर्शन करता है तो महत्वपूर्ण नुकसान हो सकता है।",
        },
        mistake3: {
          title: "बाज़ार को समय देने की कोशिश",
          text: "बाज़ार की गतिविधियों की भविष्यवाणी करने और सबसे कम बिंदु पर खरीदने जबकि सबसे ऊंचे पर बेचने का प्रयास। यहाँ तक कि पेशेवर भी बाजार टाइमिंग के साथ संघर्ष करते हैं।",
        },
        mistake4: {
          title: "योजना न होना",
          text: "स्पष्ट लक्ष्यों, समयसीमा या रणनीति के बिना निवेश करना। यह असंगत निर्णयों और खराब दीर्घकालिक परिणामों की ओर ले जाता है।",
        },
      },
      section3: {
        heading: "इन गलतियों से कैसे बचें",
        solution1: {
          title: "निवेश योजना विकसित करें",
          text: "अपने लक्ष्यों, जोखिम सहनशीलता और समयसीमा के आधार पर एक स्पष्ट रणनीति बनाएं। भावनाएं तेज़ होने पर भी अपनी योजना पर टिके रहें।",
        },
        solution2: {
          title: "अपने पोर्टफोलियो को विविधीकृत करें",
          text: "जोखिम कम करने के लिए अपने निवेश को विभिन्न एसेट क्लासेस, सेक्टरों और भौगोलिक क्षेत्रों में फैलाएं।",
        },
        solution3: {
          title: "दीर्घकालिक निवेश पर ध्यान दें",
          text: "बाज़ार को समय देने की कोशिश से बचें। इसके बजाय, नियमित रूप से निवेश करें और चक्रवृद्धि वृद्धि से लाभ उठाने के लिए दीर्घकालिक निवेशित रहें।",
        },
        solution4: {
          title: "निरंतर खुद को शिक्षित करें",
          text: "निवेश सिद्धांतों, बाज़ार की गतिशीलता और नए अवसरों के बारे में सीखते रहें। ज्ञान महंगी गलतियों के खिलाफ आपकी सबसे अच्छी रक्षा है।",
        },
      },
      section4: {
        heading: "बचने योग्य अतिरिक्त गलतियाँ",
        mistake5: {
          title: "गर्म सुझावों का पीछा करना",
          text: "अपना खुद का अनुसंधान किए बिना दोस्तों, सोशल मीडिया या वित्तीय मीडिया से स्टॉक टिप्स का पालन करना।",
        },
        mistake6: {
          title: "फीस और खर्चों को नज़रअंदाज़ करना",
          text: "इस बात पर विचार नहीं करना कि प्रबंधन फीस, लेनदेन लागत और कर समय के साथ आपके रिटर्न को कैसे घटा सकते हैं।",
        },
        mistake7: {
          title: "अति आत्मविश्वास",
          text: "यह विश्वास करना कि आप निरंतर बाज़ार को हरा सकते हैं या कि पिछली सफलता भविष्य के परिणामों की गारंटी देती है।",
        },
        mistake8: {
          title: "रीबैलेंसिंग नहीं करना",
          text: "बाज़ार मूल्यों में परिवर्तन के साथ अपने वांछित एसेट आवंटन को बनाए रखने के लिए अपने पोर्टफोलियो को समय-समय पर समायोजित करने में असफल होना।",
        },
      },
      conclusion: {
        title: "एक बेहतर निवेश रणनीति का निर्माण",
        text: "सफल निवेश सभी गलतियों से बचने के बारे में नहीं है—यह उनसे सीखने और एक अनुशासित दृष्टिकोण विकसित करने के बारे में है। इन सामान्य जालों को समझकर और ठोस निवेश सिद्धांतों को लागू करके, आप अपने वित्तीय लक्ष्यों को प्राप्त करने और दीर्घकालिक संपत्ति निर्माण की संभावनाओं में सुधार कर सकते हैं।",
        cta: "अपनी निवेश यात्रा शुरू करें",
      },
      section5: {
        heading: "निवेश में मनोवैज्ञानिक जाल",
        intro: "उन मनोवैज्ञानिक कारकों को समझना जो खराब निवेश निर्णयों को प्रेरित करते हैं, निवेश के लिए एक तर्कसंगत दृष्टिकोण विकसित करने के लिए महत्वपूर्ण है। जब वित्तीय निर्णयों की बात आती है तो हमारे दिमाग अक्सर हमारे खिलाफ काम करते हैं।",
        trap1: {
          title: "पुष्टि पूर्वाग्रह",
          text: "मौजूदा विश्वासों की पुष्टि करने वाली जानकारी की तलाश करना जबकि विरोधाभासी सबूतों को नजरअंदाज करना। यह खराब निवेश विकल्पों में अति-आत्मविश्वास की ओर ले जाता है।"
        },
        trap2: {
          title: "एंकरिंग पूर्वाग्रह",
          text: "पहली मिली जानकारी पर बहुत अधिक भरोसा करना। निवेशक अक्सर खरीद कीमतों पर एंकर हो जाते हैं और हारने वाली पोजीशन बेचने से इनकार करते हैं।"
        },
        trap3: {
          title: "हालिया पूर्वाग्रह",
          text: "निर्णय लेते समय हाल की घटनाओं को अधिक वजन देना। यह बाजार के उच्च स्तर के बाद खरीदने और निम्न स्तर के बाद बेचने का कारण बन सकता है।"
        },
        trap4: {
          title: "हानि से बचाव",
          text: "समकक्ष लाभ की तुलना में नुकसान को अधिक तीव्रता से महसूस करने की प्रवृत्ति। यह अक्सर हारने वाले निवेश को बहुत लंबे समय तक रखने और जीतने वाले को बहुत जल्दी बेचने का परिणाम होता है।"
        }
      },
      section6: {
        heading: "आयु-विशिष्ट निवेश गलतियां",
        youngInvestors: {
          title: "युवा निवेशक (20-30 के दशक)",
          description: "जबकि समय उनके पक्ष में है, युवा निवेशक अक्सर ऐसी गलतियां करते हैं जो दशकों में मिश्रित हो सकती हैं और उनके धन निर्माण को महत्वपूर्ण रूप से प्रभावित कर सकती हैं।",
          mistake1: {
            title: "बहुत रूढ़िवादी होना",
            text: "बचत खातों के साथ बहुत सुरक्षित खेलना जब उनके पास बाजार की अस्थिरता को सहने और चक्रवृद्धि वृद्धि से लाभ उठाने के लिए दशक होते हैं।"
          },
          mistake2: {
            title: "पर्याप्त जल्दी शुरू न करना",
            text: "छोटी उपलब्ध राशि के कारण निवेश में देरी करना, समय के साथ चक्रवृद्धि ब्याज के शक्तिशाली प्रभाव को खो देना।"
          }
        },
        middleAged: {
          title: "मध्यम आयु के निवेशक (40-50 के दशक)",
          description: "सेवानिवृत्ति योजना के साथ वर्तमान वित्तीय जिम्मेदारियों को संतुलित करना इस आयु समूह के लिए अनूठी चुनौतियां और सामान्य गलतियां पैदा करता है।",
          mistake1: {
            title: "पैनिक समायोजन",
            text: "बाजार में गिरावट के दौरान नाटकीय पोर्टफोलियो परिवर्तन करना, अक्सर सबसे खराब संभावित समय पर उच्च खरीदना और कम बेचना।"
          },
          mistake2: {
            title: "कैच-अप योगदान की उपेक्षा",
            text: "50 से अधिक उम्र के लोगों के लिए उपलब्ध उच्च योगदान सीमाओं का लाभ उठाने में असफल होना, सेवानिवृत्ति बचत को तेज करने के अवसरों को खो देना।"
          }
        },
        retirementAge: {
          title: "सेवानिवृत्ति-आयु निवेशक (60+)",
          description: "जो सेवानिवृत्ति के निकट या उसमें हैं वे धन संरक्षण की चुनौती का सामना करते हैं जबकि अभी भी मुद्रास्फीति से लड़ने और अपने सुनहरे वर्षों को वित्तपोषित करने के लिए रिटर्न उत्पन्न करते हैं।",
          mistake1: {
            title: "बहुत रूढ़िवादी होना",
            text: "पूरी तरह से नकद और बॉन्ड में जाना, संभावित रूप से लंबी सेवानिवृत्ति के दौरान मुद्रास्फीति के साथ तालमेल बिठाने के लिए आवश्यक कुछ विकास निवेश बनाए रखने में असफल होना।"
          },
          mistake2: {
            title: "बहुत जल्दी बहुत अधिक निकालना",
            text: "सेवानिवृत्ति की शुरुआत में अत्यधिक निकासी करना, संभावित रूप से बचत समाप्त करना और बाद के वर्षों के लिए अपर्याप्त धन छोड़ना।"
          }
        }
      },
      section7: {
        heading: "तकनीक और आधुनिक निवेश गलतियां",
        intro: "डिजिटल युग ने नए निवेश अवसर और उपकरण लाए हैं, लेकिन महंगी गलतियां करने के नए तरीके भी। इन आधुनिक जालों को समझना आज के निवेशकों के लिए आवश्यक है।",
        mistake1: {
          title: "डे ट्रेडिंग ऐप्स",
          text: "भावनाओं या सुझावों के आधार पर बार-बार ट्रेड करने के लिए मोबाइल ऐप्स का उपयोग करना, अक्सर धैर्यवान निवेश की तुलना में उच्च शुल्क और खराब रिटर्न का परिणाम होता है।"
        },
        mistake2: {
          title: "सोशल मीडिया प्रभाव",
          text: "व्यक्तिगत वित्तीय लक्ष्यों की उचित खोज या समझ के बिना सोशल मीडिया पोस्ट, प्रभावशाली लोगों या ऑनलाइन फोरम के आधार पर निवेश निर्णय लेना।"
        },
        mistake3: {
          title: "एल्गोरिदम पर अति-निर्भरता",
          text: "अंतर्निहित रणनीति को समझे बिना या यह सुनिश्चित किए बिना कि यह व्यक्तिगत परिस्थितियों और लक्ष्यों के साथ मेल खाता है, रोबो-सलाहकार सुझावों का अंधाधुंध पालन करना।"
        }
      },
      section8: {
        heading: "गलती-रोकथाम प्रणाली बनाना",
        intro: "निवेश की गलतियों से बचने का सबसे अच्छा तरीका व्यवस्थित दृष्टिकोण और सुरक्षा उपाय बनाना है जो आपको तब भी अनुशासित रहने में मदद करें जब भावनाएं तेज हों।",
        steps: {
          title: "अपनी रोकथाम प्रणाली का निर्माण"
        },
        step1: {
          title: "अपनी निवेश योजना लिखें",
          text: "अपने लक्ष्यों, जोखिम सहनशीलता, समय सीमा और रणनीति का दस्तावेजीकरण करें। इसे लिखित रूप में रखना भावनात्मक क्षणों के दौरान भटकना कठिन बना देता है।"
        },
        step2: {
          title: "स्वचालित निवेश सेट करें",
          text: "समीकरण से भावना और समय निर्णयों को हटाने के लिए नियमित योगदान को स्वचालित करें। डॉलर-कॉस्ट एवरेजिंग बाजार की अस्थिरता को चिकना करने में मदद करता है।"
        },
        step3: {
          title: "निर्णय नियम बनाएं",
          text: "खरीदने, बेचने और रीबैलेंसिंग के लिए स्पष्ट मानदंड स्थापित करें। पूर्व निर्धारित नियम होने से बाजार की अशांति के दौरान भावनात्मक निर्णय लेने से बचने में मदद मिलती है।"
        },
        step4: {
          title: "नियमित समीक्षा निर्धारित करें",
          text: "लगातार जांच करने के बजाय पोर्टफोलियो समीक्षा के लिए विशिष्ट समय निर्धारित करें। यह अल्पकालिक गतिविधियों के आधार पर अनावश्यक परिवर्तन करने के प्रलोभन को कम करता है।"
        },
        checklist: {
          title: "पूर्व-निवेश जांच सूची",
          item1: "क्या यह निवेश मेरी लिखित योजना और लक्ष्यों के साथ मेल खाता है?",
          item2: "क्या मैंने सोशल मीडिया या सुझावों से परे इसकी पूरी तरह से खोज की है?",
          item3: "क्या मैं उस पैसे का निवेश कर रहा हूं जिसे मैं खोने या लॉक करने का जोखिम उठा सकता हूं?",
          item4: "क्या मैं भावना के बजाय तर्क के आधार पर यह निर्णय ले रहा हूं?",
          item5: "यह मेरे समग्र पोर्टफोलियो विविधीकरण में कैसे फिट होता है?"
        }
      },
      section9: {
        heading: "बाजार के इतिहास से सीखना",
        intro: "बाजार का इतिहास विभिन्न आर्थिक चक्रों के दौरान निवेशकों की सामान्य गलतियों के बारे में मूल्यवान सबक प्रदान करता है। इन पैटर्न को समझना आपको उन्हें दोहराने से बचने में मदद कर सकता है।",
        lesson1: {
          title: "बाजार चक्र सामान्य हैं",
          text: "हर पीढ़ी बाजार क्रैश और रिकवरी का अनुभव करती है। जो लोग पूर्ण चक्रों के दौरान निवेशित रहते हैं वे आम तौर पर उन लोगों से बेहतर प्रदर्शन करते हैं जो बाजार को समय देने की कोशिश करते हैं।"
        },
        lesson2: {
          title: "बुलबुले और उन्माद दोहराते हैं",
          text: "इतिहास भर में, निवेशक बार-बार सट्टा बुलबुलों में फंसते रहे हैं, ट्यूलिप से लेकर प्रौद्योगिकी स्टॉक तक। संकेतों को पहचानना आपको इन जालों से बचने में मदद कर सकता है।"
        },
        lesson3: {
          title: "बाजार में समय बाजार को समय देने से बेहतर है",
          text: "ऐतिहासिक डेटा लगातार दिखाता है कि जो निवेशक लंबी अवधि तक निवेशित रहते हैं वे उन लोगों से बेहतर प्रदर्शन करते हैं जो भविष्यवाणियों के आधार पर बाजार में आने-जाने की कोशिश करते हैं।"
        }
      }
    }
  }
};
